import React from 'react';
import ReactDOM from 'react-dom';
import { toLower } from 'lodash';
import {
  applyRouterMiddleware,
  Router,
  Redirect,
  Route,
  browserHistory,
} from 'react-router';
import { WebUIThemeProvider } from 'web-ui-components/lib/utilities/themes';
import envConfig from '../../static/config';
import { dybToB } from '../utils/ResourceLoaderUtils';
import { getBrandId } from '../utils/BrandUtils';
import InternetBanking from '../components/internet-banking/InternetBanking';
import BankApp from '../components/BankApp';
import { SorryPage } from '../components/internet-banking/SorryPage';
import ErrorPage from '../components/internet-banking/ErrorPage';
import NonBCustomerError from '../components/common/NonBCustomerError';
import InvalidTokenSessionTimeOutPage from '../components/common/InvalidTokenSessionTimeOutPage';
import { ContactBankPage } from '../components/internet-banking/ContactBankPage';
import { AccountLockedPage } from '../components/internet-banking/AccountLockedPage';
import UserDetailsErrorPage from '../components/common/UserDetailsErrorPage';
import { SetupInProgressPage } from '../components/internet-banking/SetupInProgressPage';
import { DefaultBankSelector } from '../components/DefaultBankSelector';
import { RequiresSME } from '../components/RequiresSME';
import { withLCV } from '../components/withLCV';
import HomePage from '../components/homepage/Homepage';
import { MTrackPage } from '../components/internet-banking/MTrackPage';

// Static PL Pages
import PagePreferencesOrder from '../pages/PagePreferencesOrder';
import PagePreferencesShowHide from '../pages/PagePreferencesShowHide';
import PagePreferencesSetNickname from '../pages/PagePreferencesSetNickname';
import PagePreferencesAlerts from '../pages/PagePreferencesAlerts';
import { Projections } from '../components/financial-stories/projections/Projections';
import TileComponent from '../components/financial-stories/TileComponent';
import AccountDetailsComponent from '../components/financial-stories/AccountDetailsNew/AccountDetailsComponent';

import OrderComponent from '../components/financial-stories/AccountDetailsNew/OrderComponent';
import DigitalStatements from '../components/financial-stories/DigitalStatements';
import StatementPreferences from '../components/financial-stories/StatementPreferences';
import PaperFreePreferences from '../components/financial-stories/PaperFreePreferences';
import ManagePaymentsLandingPage from '../components/managepayments/ManagePaymentsLandingPage';
import EditPayment from '../components/managepayments/EditPayment';
import EditPaymentSuccess from '../components/managepayments/EditPayment/EditPaymentSuccess';
import ViewPayment from '../components/managepayments/ViewPayment';
import MoveMoney from '../components/movemoney/MoveMoneyPage';
import { withBatchPayments } from '../components/movemoney/sme-user/WithBatchPayments';
import SpendingsPage from '../components/spendings/SpendingsPage';
import { SavingPotsPage } from '../components/saving-pots/SavingPotsPage';
import HelpHomeComponent from '../components/help/HelpHomeComponent';
import AnyQuestions from '../components/help/AnyQuestions';
import { LogoutPage } from '../components/logout/LogoutPage';
import Product from '../components/products/Product';
import BrowserUtils from '../utils/BrowserUtils';
import InternetBankingContentStore from '../stores/InternetBankingContentStore';
import AccountPreference from '../components/accountpreference/AccountPreference';
import RenameAccountsLandingPage from '../components/accountpreference/RenameAccountsLandingPage';
import CommsHub from '../components/securemessage/CommsHubLandingPage';
import BeneficiarySync from '../components/movemoney/BeneficiarySync';
import ActivationPage from '../components/verification/ActivationContainer';
import ExportTransactionLanding from '../components/export-transactions/ExportTransactionLanding';

import BrowserFormFactor from '../components/payment-fate/BrowserFormFactor';
import PaymentHistoryList from '../components/payment-fate/PaymentStatusHome';
import MasterCardAlertsLanding from '../components/mastercard-alerts/MasterCardAlertsLanding';
import Inner from '../components/Inner';
import AccountPickerPage from '../components/common/AccountPickerPage';
import LoaderComponent from '../components/common/LoaderComponent';
import CardManagementLanding from '../components/card-management/CardManagementLanding';

import AccountPools from '../components/manageAccountPools/AccountPools';
import CreateAccountPool from '../components/manageAccountPools/CreateAccountPool/CreateAccountPool';
import { GetPoolDetails } from '../components/manageAccountPools/GetPoolDetails/GetPoolDetails';
import ManagePersonalDetails from '../components/ManagePersonalDetails/ManagePersonalDetails';
import { ManageInsurance } from '../components/manageInsurance/ManageInsurance';
import { PrivateBankingBenefits } from '../components/banking-benefits/PrivateBankingBenefits';
import { CurrentTrackerBenefits } from '../components/banking-benefits/CurrentTrackerBenefits';

import { LastLoginPage } from '../components/last-login';
import { ContactPreferences } from '../components/contact-preferences';
import { GivingNoticePage } from '../components/giving-notice/GivingNoticePage';

const getPageUrl = (segment) => {
  return `/${toLower(dybToB(envConfig.brandId))}/${segment}`;
};

const AppRouter = {
  routes: (
    <Route
      path={`/${toLower(dybToB(envConfig.brandId))}`}
      component={DefaultBankSelector(BankApp)}
    >
      <Route component={InternetBanking}>
        <Route path={getPageUrl('homepage')} component={withLCV(HomePage)} />
        <Route
          path={getPageUrl('pagepreferencesorder')}
          component={PagePreferencesOrder}
        />
        <Route
          path={getPageUrl('pagepreferencesshowhide')}
          component={PagePreferencesShowHide}
        />
        <Route
          path={getPageUrl('pagepreferencessetnickname')}
          component={PagePreferencesSetNickname}
        />
        <Route
          path={getPageUrl('pagepreferencesalerts')}
          component={PagePreferencesAlerts}
        />
        <Route
          path={getPageUrl('account-view')}
          component={AccountDetailsComponent}
        />

        <Route path={getPageUrl('orderstatement')} component={OrderComponent} />
        <Route path={getPageUrl('orderpayin')} component={OrderComponent} />
        <Route path={getPageUrl('ordercheque')} component={OrderComponent} />

        <Route path={getPageUrl('statements')} component={DigitalStatements} />
        <Route
          path={getPageUrl('statementspreferences')}
          component={
            envConfig.isPaperFreePreferencesEnabled
              ? PaperFreePreferences
              : StatementPreferences
          }
        />

        <Route
          path={getPageUrl('movemoney')}
          component={RequiresSME(withBatchPayments(MoveMoney))}
        >
          <Route
            path={getPageUrl('movemoney/batchpayments')}
            component={withBatchPayments(MoveMoney)}
          />
          <Route path={getPageUrl('movemoney/review')} component={MoveMoney} />
          <Route
            path={getPageUrl('movemoney/submitted')}
            component={MoveMoney}
          />
          <Route
            path={getPageUrl('movemoney/repeat-payment')}
            component={MoveMoney}
          />
        </Route>
        <Route path={getPageUrl('product')} component={Product} />
        <Route
          path={getPageUrl('managepayment')}
          component={RequiresSME(ManagePaymentsLandingPage)}
        />
        <Route path={getPageUrl('viewpayment')} component={ViewPayment} />
        <Route
          path={getPageUrl('editpayment')}
          component={RequiresSME(EditPayment)}
        />
        <Route
          path={getPageUrl('successEdit')}
          component={EditPaymentSuccess}
        />
        <Route
          path={getPageUrl('beneficiarysync')}
          component={BeneficiarySync}
        />

        <Route path={getPageUrl('savingpots')} component={SavingPotsPage} />
        <Route path={getPageUrl('projections')} component={Projections} />
        <Route
          path={getPageUrl('financialStories')}
          component={TileComponent}
        />
        <Route path={getPageUrl('spendings')} component={SpendingsPage} />

        <Route
          path={getPageUrl('alertsnsweeps')}
          component={MasterCardAlertsLanding}
        />
        <Route path={getPageUrl('help')} component={HelpHomeComponent} />
        <Route path={getPageUrl('faq')} component={AnyQuestions} />
        <Route path={getPageUrl('logout')} component={LogoutPage} />
        <Route path={getPageUrl('sorry')} component={SorryPage} />
        <Route path={getPageUrl('error')} component={ErrorPage} />
        <Route path={getPageUrl('nonberror')} component={NonBCustomerError} />
        <Route
          path={getPageUrl('session-expired')}
          component={InvalidTokenSessionTimeOutPage}
        />
        <Route path={getPageUrl('contact-bank')} component={ContactBankPage} />
        <Route
          path={getPageUrl('account-locked')}
          component={AccountLockedPage}
        />
        <Route
          path={getPageUrl('setup-in-progress')}
          component={SetupInProgressPage}
        />
        <Route path={getPageUrl('not-found')} component={ErrorPage} />
        <Route
          path={getPageUrl('accountpreference')}
          component={AccountPreference}
        />
        <Route
          path={getPageUrl('securemessage')}
          component={RequiresSME(CommsHub)}
        />
        <Route
          path={getPageUrl('my-documents')}
          component={RequiresSME(CommsHub)}
        />
        <Route
          path={getPageUrl('exporttransaction')}
          component={ExportTransactionLanding}
        />
        <Route
          path={getPageUrl('renameaccount')}
          component={RenameAccountsLandingPage}
        />
        <Route
          path={getPageUrl('activateYourSecurityToken')}
          component={ActivationPage}
        />
        <Route path={getPageUrl('inner')} component={Inner} />
        <Route
          path={getPageUrl('paymentStatus')}
          component={RequiresSME(BrowserFormFactor(PaymentHistoryList))}
        />
        <Route
          path={getPageUrl('error-page')}
          component={UserDetailsErrorPage}
        />
        <Route
          path={getPageUrl('accountpicker')}
          component={AccountPickerPage}
        />
        <Route
          path={getPageUrl('manage-cards')}
          component={CardManagementLanding}
        />
        <Route
          exact
          path={getPageUrl('account-pool')}
          component={AccountPools}
        />
        <Route
          path={getPageUrl('account-pool/create')}
          component={CreateAccountPool}
        />
        <Route
          path={getPageUrl('account-pool/get')}
          component={GetPoolDetails}
        />
        <Route
          path={getPageUrl('manage-details')}
          component={ManagePersonalDetails}
        />
        <Route
          path={getPageUrl('contact-preferences')}
          component={ContactPreferences}
        />
        <Route
          path={getPageUrl('manage-insurance')}
          component={ManageInsurance}
        />
        <Route
          path={getPageUrl('private-banking-benefits')}
          component={PrivateBankingBenefits}
        />
        <Route
          path={getPageUrl('current-tracker-benefits')}
          component={CurrentTrackerBenefits}
        />
        <Route path={getPageUrl('last-login')} component={LastLoginPage} />
        <Route
          path={getPageUrl('handle-logout')}
          component={() => <LoaderComponent type="large" />}
        />
        <Route path={getPageUrl('m-track')} component={MTrackPage} />
        <Route
          path={getPageUrl('giving-notice')}
          component={GivingNoticePage}
        />
        <Redirect from="*" to={getPageUrl('not-found')} query={{}} />
        <Redirect from={getPageUrl('authentication')} to="homepage" />
      </Route>
    </Route>
  ),
  /**
   * Kick off route handling.
   */
  init() {
    const reactElement = document.getElementById('app');

    if (!BrowserUtils.isCompatible()) {
      reactElement.innerHTML = InternetBankingContentStore.get(
        'upgradeBrowserMessage'
      );
      return false;
    }

    ReactDOM.render(
      <WebUIThemeProvider brandID={getBrandId(envConfig.brandId)}>
        <Router
          history={browserHistory}
          routes={this.routes}
          render={applyRouterMiddleware()}
        />
      </WebUIThemeProvider>,
      reactElement
    );
  },
};

export default AppRouter;
